import React, { useCallback, useState } from "react";
import { PowerPickTimeZoneToLocal } from "/app/src/components/generic/formatting/dateTime";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Material as MaterialType,
  MaterialProperty as MaterialPropertyType,
  Bin,
  StorageRule,
  LocationContent as LocationContentType,
} from "/app/src/models";
import {
  materialService,
  materialPropertyService,
  binService,
  storageRuleService,
  locationContentService,
} from "/app/src/services";
import { useTranslation } from "react-i18next";
import {
  handlePromiseError,
  useHandleNavigationError,
} from "/app/src/helpers/api";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";
import { AccordionItem, Accordion, Divider } from "@heroui/react";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import InfoList from "/app/src/components/generic/components/explorer/infoList";
import Box from "/app/src/components/generic/components/box";
import { buildParams } from "/app/src/helpers";
import StorageRules from "/app/src/components/explorer/materials/material/editStorageRule";
import NewStorageRule from "/app/src/components/explorer/materials/material/newStorageRule";
import ModelItems from "/app/src/components/generic/components/explorer/modelItems";
import BreakdownList from "./BreakdownList";

const TRANSACTION_DYNAMIC_IGNORE_KEYS = new Set<string>([
  "id",
  "name",
  "materialPropertyId",
  "materialFamilyId",
  "creationDate",
  "weightPerUnit",
  "valuePerUnit",
  "unitOfMeasure",
  "reOrderPoint",
  "canOrderPoint",
  "isBlocked",
  "blockReason",
  "Info1",
  "Info2",
  "Info3",
  "Info4",
  "Info5",
  "codes",
  "currentQuantity",
  "expectedQuantity",
  "currentValue",
  "putQuantity",
  "pickQuantity",
  "netQuantity",
  "materialProperty",
  "warehouseQuantities",
  "locationCount",
  "countingCycle",
]);

interface FormValues {
  binId?: string;
  minStockPerBin?: number;
  maxStockPerBin?: number;
  requiredCapacity?: number;
  isDefaultBin?: number;
}

/**
 * Formats form values into the required data structure for processing.
 *
 * @param materialId - The ID of the material.
 * @param values - The form values including bin information and stock limits.
 * @returns The formatted object containing material and bin settings.
 */
function formatForm(materialId: string, values: FormValues) {
  return {
    materialId,
    binId: values.binId,
    minStockPerBin: values.minStockPerBin,
    maxStockPerBin: values.maxStockPerBin,
    requiredCapacity: values.requiredCapacity,
    isDefaultBin: values.isDefaultBin,
  };
}

/**
 * Material component renders the material details view.
 *
 * @returns {JSX.Element} The Material component.
 */
export default function Material() {
  const { t } = useTranslation();
  const { materialId } = useParams();
  const handleNavigationError = useHandleNavigationError();
  const [dynamicFields, setDynamicFields] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const queryClient = useQueryClient();

  const toggleIndicatorIcon = useCallback(({ isOpen }) => {
    return isOpen ? (
      <IconBuilder icon="ChevronRight" />
    ) : (
      <IconBuilder icon="ChevronDown" />
    );
  }, []);

  const { data: material } = useQuery({
    queryKey: ["material", materialId],
    queryFn: () => {
      return materialService.getSingle(materialId).catch(() => {
        handleNavigationError();
      });
    },
    initialData: { material: {} },
    retry: false,
    select: (data: { material: MaterialType }) => {
      return data.material;
    },
    enabled: Boolean(materialId),
    onSuccess: (data?: MaterialType) => {
      if (!data) return;

      const newDynamicFields = Object.entries(data)
        .filter(
          ([key, value]) =>
            !TRANSACTION_DYNAMIC_IGNORE_KEYS.has(key) && value !== null,
        )
        .map(([key, value]) => ({
          label: key,
          value: String(value),
        }));
      setDynamicFields(newDynamicFields);
    },
  });

  const { data: bins } = useQuery({
    queryKey: ["bins"],
    queryFn: () => {
      return binService.getAll();
    },
    initialData: { bins: [] },
    select: (data: { bins: Bin[] }) => {
      return data.bins;
    },
  });

  const { data: warehouseBreakdown } = useQuery({
    queryKey: ["warehouseBreakdown", material?.id],
    queryFn: () => {
      return materialService.getMaterialWarehouseBreakdown(material?.id);
    },
    initialData: [],
    select: (data) => {
      return data;
    },
    enabled: Boolean(material?.id),
  });

  const { data: storageUnitBreakdown } = useQuery({
    queryKey: ["storageUnitBreakdown", material?.id],
    queryFn: () => {
      return materialService.getMaterialStorageUnitBreakdown(material?.id);
    },
    initialData: [],
    select: (data) => {
      return data;
    },
    enabled: Boolean(material?.id),
  });

  const { data: carrierBreakdown } = useQuery({
    queryKey: ["carrierBreakdown", material?.id],
    queryFn: () => {
      return materialService.getMaterialCarrierBreakdown(material?.id);
    },
    initialData: [],
    select: (data) => {
      return data;
    },
    enabled: Boolean(material?.id),
  });

  const { data: shelvesBreakdown } = useQuery({
    queryKey: ["shelvesBreakdown", material?.id],
    queryFn: () => {
      return materialService.getMaterialShelvesBreakdown(material?.id);
    },
    initialData: [],
    select: (data) => {
      return data;
    },
    enabled: Boolean(material?.id),
  });

  const { data: storageRules } = useQuery({
    queryKey: ["storage_rules", material?.id],
    queryFn: () => {
      return storageRuleService.getAll(
        buildParams({ materialId: material?.id }),
      );
    },
    initialData: { storage_rules: [] },
    select: (data: { storage_rules: StorageRule[] }) => {
      return data.storage_rules;
    },
    enabled: Boolean(material?.id),
  });

  const { data: locationContents = [] } = useQuery({
    queryKey: ["locationContentsByMaterial", material.id],
    queryFn: () => {
      return locationContentService.getAll(
        buildParams({ materialId: material.id }),
      );
    },
    enabled: Boolean(material?.id),
    initialData: { contents: [] },
    select: (data: { contents: LocationContentType[] }) => {
      return data.contents;
    },
  });

  /**
   * Gets information for each location content, including id, name, and current quantity.
   * @returns {Array<{id: string; name: string; value: string}>} Array of location content info objects.
   */
  const getLocationContentInfo = () => {
    return locationContents.map((content) => ({
      id: content.locationId,
      name: content.locationName,
      value: String(content.currentQuantity),
    }));
  };

  const { data: materialProperty } = useQuery({
    queryKey: ["materialProperty", material.materialPropertyId],
    queryFn: () => {
      return materialPropertyService.getSingle(material.materialPropertyId);
    },
    initialData: { material_property: {} },
    select: (data: { material_property: MaterialPropertyType }) => {
      return data.material_property;
    },
    enabled: Boolean(material.materialPropertyId),
  });

  const { mutateAsync: createStorageRule } = useMutation({
    mutationFn: (storageRule: StorageRule) => {
      return storageRuleService
        .createSingle(storageRule)
        .then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["storage_rules", material?.id],
        (oldData: { storage_rules: StorageRule[] }) => {
          return {
            storage_rules: [...oldData.storage_rules, response.storage_rule],
          };
        },
      );
    },
  });

  const addStorageRuleHandler = useCallback(
    async (values: FormValues) => {
      const storageRule = {
        ...formatForm(material?.id, values),
      };
      await createStorageRule(storageRule);
    },
    [createStorageRule, material?.id],
  );

  const basicInfo = [
    { label: t("translation:name"), value: material?.name },
    {
      label: t("translation:codes"),
      value: material?.codes?.join(", "),
    },
    {
      label: t("translation:creationDate"),
      value: (
        <PowerPickTimeZoneToLocal
          date={material?.creationDate}
          format={"MMM DD, YYYY HH:mm"}
        />
      ),
    },
    {
      label: t("translation:blocked"),
      value: material?.isBlocked ? t("translation:yes") : t("translation:no"),
    },
    { label: t("translation:block_reason"), value: material?.blockReason },
    {
      label: t("translation:material_property"),
      value: materialProperty?.name,
      link: `/explorer/material-properties/${materialProperty?.id}`,
    },
  ];

  const infoFields = [
    { label: t("translation:info1"), value: material?.Info1 },
    { label: t("translation:info2"), value: material?.Info2 },
    { label: t("translation:info3"), value: material?.Info3 },
    { label: t("translation:info4"), value: material?.Info4 },
    { label: t("translation:info5"), value: material?.Info5 },
  ];

  const materialConfiguration = [
    { label: t("translation:unit_of_measure"), value: material?.unitOfMeasure },
    { label: t("translation:value_per_unit"), value: material?.valuePerUnit },
    { label: t("translation:weight_per_unit"), value: material?.weightPerUnit },
    { label: t("translation:reorder_point"), value: material?.reOrderPoint },
    { label: t("translation:canorder_point"), value: material?.canOrderPoint },
    { label: t("translation:counting_cycle"), value: material?.countingCycle },
  ];

  const quantityMetrics = [
    {
      label: t("translation:current_quantity"),
      value: material?.currentQuantity,
    },
    { label: t("translation:pick_quantity"), value: material?.pickQuantity },
    { label: t("translation:put_quantity"), value: material?.putQuantity },
    { label: t("translation:net_quantity"), value: material?.netQuantity },
    { label: t("translation:location_count"), value: material?.locationCount },
  ];

  return (
    <div className="explorer">
      <Helmet>
        <title>{material.name}</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: "Materials", to: "/explorer/materials" },
          { label: material.name },
        ]}
      />
      {material?.id && (
        <>
          <h1 className="text-[36px] font-medium">{material?.name}</h1>
          <h2 className="text-[16px] mb-6 text-neutral-smoke">
            Material Id: {material?.id}
          </h2>
          <InfoList
            header={t("translation:basic_info")}
            items={basicInfo}
            infoFields={infoFields}
          />
          <InfoList
            header={t("translation:material_configuration")}
            items={materialConfiguration}
          />
          <InfoList
            header={t("translation:quantity_metrics")}
            items={quantityMetrics}
          />
          {dynamicFields.length > 0 && (
            <InfoList
              header={t("translation:dynamic_fields")}
              items={dynamicFields}
            />
          )}
          <Box>
            <h3 className="text-[16px] font-medium mb-4">
              {t("translation:storage_rules")}
            </h3>

            {storageRules.map((rule) => {
              const bin = bins.find((b) => b.id === rule.binId);
              return (
                <StorageRules
                  key={rule.id}
                  storageRule={rule}
                  binName={bin?.name}
                  material={material}
                  bins={bins}
                />
              );
            })}
            <Divider className="my-6" />
            <NewStorageRule
              storageRules={storageRules}
              bins={bins}
              onCreate={addStorageRuleHandler}
            />
          </Box>
        </>
      )}
      {(warehouseBreakdown?.length > 0 ||
        storageUnitBreakdown?.length > 0 ||
        carrierBreakdown?.length > 0 ||
        shelvesBreakdown?.length > 0) && (
        <Box>
          <Accordion
            selectionMode="single"
            showDivider
            itemClasses={{ trigger: "py-0" }}
          >
            <AccordionItem
              key="breakdown"
              title={
                <h3 className="text-[16px] font-medium mb-0">
                  {t("translation:quantity_breakdown")}
                </h3>
              }
              indicator={toggleIndicatorIcon}
            >
              <div className="mt-2">
                {warehouseBreakdown && warehouseBreakdown.length > 0 && (
                  <BreakdownList
                    title={t("translation:warehouses")}
                    items={warehouseBreakdown}
                  />
                )}

                {storageUnitBreakdown && storageUnitBreakdown.length > 0 && (
                  <>
                    <Divider className="my-6" />
                    <BreakdownList
                      title={t("translation:storage_units")}
                      items={storageUnitBreakdown}
                    />
                  </>
                )}

                {carrierBreakdown && carrierBreakdown.length > 0 && (
                  <>
                    <Divider className="my-6" />
                    <BreakdownList
                      title={t("translation:carriers")}
                      items={carrierBreakdown}
                    />
                  </>
                )}

                {shelvesBreakdown && shelvesBreakdown.length > 0 && (
                  <>
                    <Divider className="my-6" />
                    <BreakdownList
                      title={t("translation:shelves")}
                      items={shelvesBreakdown}
                    />
                  </>
                )}
              </div>
            </AccordionItem>
          </Accordion>
        </Box>
      )}

      {getLocationContentInfo().length > 0 && (
        <ModelItems
          modelName={t("translation:locations")}
          items={getLocationContentInfo()}
        />
      )}
    </div>
  );
}
